<template>
  <div class="info-view">
    <template v-if="isMobile">
      <main-header
        :active-category="data.activeCategory"
        @expandAll="toggleExpandAll"
        @show-share-overlay="handleShareClick"
      />

      <div v-if="data.isTryNowBannerVisible" class="try-now-banner">
        <span>Create your own digital guidebook!</span>
        <a href="#" @click.prevent="handleTryNowClick">Try now</a>
        <i class="icon-Cross" @click="hideTryNowBanner"></i>
      </div>
    </template>

    <div
      class="info-view__row"
      :class="{ 'info-view__row-with--banner': data.isTryNowBannerVisible }"
    >
      <div class="info-view__column categories" :class="{ hidden: !showCategoryDialog }">
        <div class="info-view__categories">
          <template v-if="!isMobile">
            <main-header
              :active-category="data.activeCategory"
              @expandAll="toggleExpandAll"
              @show-share-overlay="handleShareClick"
            />

            <div v-if="data.isTryNowBannerVisible" class="try-now-banner">
              <span>Create your own digital guidebook!</span>
              <a href="#" @click.prevent="handleTryNowClick">Try now</a>
              <i class="icon-Cross" @click="hideTryNowBanner"></i>
            </div>
          </template>

          <!-- Categories -->
          <aside
            aria-label="Categories"
            ref="categoryScrollContainerRef"
            class="category-card__scroll"
          >
            <div class="category-card__wrapper" :class="{ info: isInfoPage }">
              <template v-if="categories.length">
                <category-card
                  v-for="category in categories"
                  :id="category.id"
                  :key="category.id"
                  :category="category"
                  :active-category="data.activeCategory"
                  @collapse-change="onCategoryCollapseChange"
                />
              </template>
            </div>
          </aside>
        </div>
      </div>

      <!-- Desktop Right Side (Subcategory & Topics) -->
      <section class="info-view__column category-topic" :class="{ hidden: showCategoryDialog }">
        <!-- Right Side Header -->
        <el-row
          align="middle"
          justify="space-between"
          class="info-view__category"
          :style="{ backgroundColor: theme.accentColor }"
        >
          <h2>{{ activeSubcategoryTitle }}</h2>

          <div class="expand-row">
            <i
              class="icon-Share-3"
              @click="handleShareClick({ type: ShareTypesEnum.SUBCATEGORY })"
            ></i>
            <i
              role="button"
              aria-label="Collapse All"
              class="icon-Collapse-All"
              @click="toggleExpandAll(false)"
            ></i>

            <i
              role="button"
              aria-label="Expand All"
              class="icon-Expand-All"
              @click="toggleExpandAll(true)"
            ></i>
          </div>
        </el-row>

        <!-- Right Side -->
        <section v-show="isRightSideVisible" ref="topicContainerRef" class="info-view__content">
          <div class="info-view__content--wrapper">
            <el-collapse
              v-for="topic in activeTopics"
              :key="topic.id"
              v-model="data.expandedTopicIds"
            >
              <weather-topic
                v-if="topic.dynamic_content_type === TopicContentTypeEnum.WEATHER"
                @show-share-overlay="handleShareClick"
                :weather-id="topic.id"
                :topic-title="topic.translations[helperStore.currentLanguage].title"
              />

              <rating-topic
                v-if="
                  topic.dynamic_content_type === TopicContentTypeEnum.RATING ||
                  topic.dynamic_content_type === TopicContentTypeEnum.FIRST_IMPRESSIONS ||
                  topic.dynamic_content_type === TopicContentTypeEnum.EMAIL_CAPTURE
                "
                @show-share-overlay="handleShareClick"
                :topic="topic"
              />

              <tides-topic
                v-if="topic.dynamic_content_type === TopicContentTypeEnum.TIDE"
                @show-share-overlay="handleShareClick"
                :is-topic-expanded="!!data.expandedTopicIds[0]"
                :topic="topic"
              />

              <!-- regular, lockcode, reservation -->
              <regular-topic
                v-if="isTopicRegularLockcodeOrReservation(topic)"
                @show-share-overlay="handleShareClick"
                :topic="topic"
                :is-topic-expanded="data.expandedTopicIds.includes(topic.id)"
              />
            </el-collapse>
          </div>
        </section>
      </section>
    </div>

    <mobile-subcategories-dialog
      v-if="data.activeCategory && showCategoryDialog"
      :active-category="data.activeCategory"
      :scroll-position="data.mobileSubcategoriesScrollPosition"
      @update-scroll-position="data.mobileSubcategoriesScrollPosition = $event"
      @close="data.activeCategory = null"
      @show-share-overlay="handleShareClick"
    />

    <share-overlay
      v-show="data.shareLink"
      :share-link="data.shareLink"
      @close="data.shareLink = null"
    />
  </div>
</template>

<script lang="ts" setup>
import CategoryCard from '@/components/CategoryCard.vue'
import RegularTopic from '@/components/RegularTopic.vue'
import flatten from 'lodash/flatten'
import MainHeader from '@/components/MainHeader.vue'
import WeatherTopic from '@/components/WeatherTopic/WeatherTopic.vue'
import ShareOverlay from '@/components/ShareOverlay.vue'
import { isMobile as isMobileFunction, sendPlausibleEvent } from '@/utils/helpers'
import MobileSubcategoriesDialog from '@/components/MobileSubcategoriesDialog.vue'
import {
  Category,
  ShareTypesEnum,
  Subcategory,
  Topic,
  TopicContentTypeEnum
} from '@/interfaces/guide.interface'
import RouteNamesEnum from '@/router/route-names'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import { computed, onActivated, onMounted, reactive, ref, watch } from 'vue'
import RatingTopic from '@/components/RatingTopic.vue'
import TidesTopic from '@/components/TidesTopic.vue'
import { useHelperStore } from '@/pinia/helper.store'
import { useMainStore } from '@/pinia/main.store'
import { PlausibleEvents, PlausibleProperties, TS_IGNORE_NEW_GUIDE_MESSAGE } from '@/utils/consts'

const helperStore = useHelperStore()
const mainStore = useMainStore()

const router = useRouter()
const route = useRoute()

let stashedPath = ''
let stashedScrollPosition = 0
const isMobile = isMobileFunction()

const data = reactive({
  activeCategory: null as null | Category,
  expandedTopicIds: [] as number[],
  mobileSubcategoriesScrollPosition: 0,
  shareLink: null as string,
  isTryNowBannerVisible: false
})

const topicContainerRef = ref<HTMLDivElement>(null)
const categoryScrollContainerRef = ref<HTMLDivElement>(null)

const activeTopics = computed(() => mainStore.activeSubcategory?.topics || [])
const isInfoPage = computed(() => route.name === RouteNamesEnum.INFO)
const isRightSideVisible = computed(() => !!activeTopics.value.length)
const categories = computed(() =>
  isInfoPage.value ? mainStore.infoCategories : mainStore.guideCategories
)
const showCategoryDialog = computed(() => isMobile && activeTopics.value.length === 0)
const activeSubcategoryTitle = computed(
  () => mainStore.activeSubcategory?.title_translations?.[helperStore.currentLanguage] || ''
)
const theme = computed(() => helperStore.styleData)
const shouldStashBeChecked = computed(() => helperStore.shouldStashBeChecked)

watch(route, (newRoute) => {
  const subcategoryId = +newRoute.params.subcategoryId

  // No active category, no active subcategory
  if (!subcategoryId && !newRoute.query.categoryId) {
    data.activeCategory = null
    resetActiveSubcategory()

    return
  }

  // Active category
  if (newRoute.query.categoryId) {
    data.activeCategory = categories.value.find((c) => c.id === +newRoute.query.categoryId)

    if (isMobile) {
      // view track for query
      sendPlausibleEvent(PlausibleEvents.CATEGORY_VIEW, {
        [PlausibleProperties.CATEGORY_ID]: data.activeCategory.id
      })

      resetActiveSubcategory()
    } else {
      setFirstSubcategory()
      scrollToActiveCategory()
    }

    return
  }

  if (!subcategoryId) {
    return
  }

  // Active subcategory
  handleSubcategoryChange(subcategoryId)

  // Active topic
  if (newRoute.hash) {
    handleRouteHash(+route.hash.slice(1), subcategoryId)
  }
})

watch(
  () => data.expandedTopicIds,
  (newIds, oldIds) => {
    // one new item expanded & not switched to new subcategory
    if (newIds.length - oldIds.length === 1 && newIds.length !== activeTopics.value.length) {
      scrollToTopic(newIds[newIds.length - 1])
    }
  }
)

onActivated(() => {
  showTryNowBannerIfNeeded('guide_banner_non_unique_impression')

  if (shouldStashBeChecked.value) {
    checkStash()
  } else {
    helperStore.shouldStashBeChecked = true // reset the stash check
  }
})

onBeforeRouteLeave((from, to, next) => {
  if (topicContainerRef.value) {
    stashedScrollPosition = topicContainerRef.value.scrollTop
  }

  stashedPath = route.fullPath

  next()
})

onMounted(() => {
  showTryNowBannerIfNeeded('guide_banner_unique_impression')

  const subcategoryId = route.params.subcategoryId

  if (subcategoryId) {
    handleSubcategoryChange(+subcategoryId)

    if (route.hash) {
      handleRouteHash(+route.hash.slice(1), +subcategoryId)
    }

    return
  }

  if (route.query.categoryId) {
    if (isMobile) {
      resetActiveSubcategory()
    }

    data.activeCategory = categories.value.find((c) => c.id === +route.query.categoryId)

    scrollToActiveCategory()
  }

  // By default, select first category & subcategory for desktop view
  if (!isMobile) {
    setFirstSubcategory()
  }
})

function handleSubcategoryChange(subcategoryId: number) {
  const subcategory = getSubcategory(subcategoryId)
  const category = categories.value.find((c) =>
    c.subcategories.find((s) => s.id === +subcategoryId)
  )

  if (subcategory) {
    mainStore.updateActiveSubcategory(subcategory)
    scrollToActiveCategory()
    scrollToTop()

    if (subcategory.expand_topics) {
      toggleExpandAll(true)
    }
  }

  data.activeCategory = category
}

function checkStash() {
  if (stashedPath) {
    router.replace(stashedPath).catch(() => {
      /* ignore redundant navigation error */
    })
  }

  setTimeout(() => {
    if (stashedScrollPosition) {
      topicContainerRef.value.scrollTo({
        behavior: 'smooth',
        left: 0,
        top: stashedScrollPosition
      })
    }
  }, 500)
}

function expandAllTopics(topics: Topic[]) {
  data.expandedTopicIds = topics.map((t) => t.id)
}

function showTryNowBannerIfNeeded(
  event: 'guide_banner_non_unique_impression' | 'guide_banner_unique_impression'
) {
  data.isTryNowBannerVisible =
    mainStore.isBannerActive &&
    localStorage.getItem(TS_IGNORE_NEW_GUIDE_MESSAGE + helperStore.guideToken) !== 'true'

  if (data.isTryNowBannerVisible) {
    window.dataLayer.push({ event })
  }
}

function scrollToTopic(topicId: number) {
  setTimeout(() => {
    const topicEl = document.getElementById(topicId.toString())

    if (!topicEl) {
      return
    }

    topicContainerRef.value.scrollTo({
      behavior: 'smooth',
      left: 0,
      top: topicEl.offsetTop - 10
    })
  }, 500)
}

function scrollToActiveCategory() {
  if (!data.activeCategory) {
    return
  }

  setTimeout(() => {
    const topicEl = document.getElementById(data.activeCategory.id?.toString())

    if (topicEl) {
      categoryScrollContainerRef.value.scrollTo({
        behavior: 'smooth',
        left: 0,
        top: topicEl.offsetTop - 96
      })
    }
  }, 500)
}

function getSubcategory(subcategoryId: number | string): Subcategory {
  const { guideCategories, infoCategories } = mainStore
  const categories = [...guideCategories, ...infoCategories]
  const allSubcategories = flatten(categories.map((c) => c.subcategories))

  return allSubcategories.find((subcategory) => subcategory.id === +subcategoryId)
}

function hideTryNowBanner() {
  localStorage.setItem(TS_IGNORE_NEW_GUIDE_MESSAGE + helperStore.guideToken, 'true')
  data.isTryNowBannerVisible = false
}

function handleRouteHash(topicId: number, subcategoryId: number) {
  const category = categories.value.find((c) =>
    c?.subcategories.find((s) => s.id === subcategoryId)
  )
  const subcategory = category.subcategories.find((s) => s.id === subcategoryId)
  const topic = subcategory.topics.find((t) => topicId === t.id || topicId === t.original_id)

  scrollToActiveCategory()

  if (topic) {
    if (!data.expandedTopicIds.includes(topic.id)) {
      data.expandedTopicIds.push(topic.id)
    }

    scrollToTopic(topic.id)
  }
}

function setFirstSubcategory() {
  const firstCategory = data.activeCategory || categories.value[0] // if no active category, set first one
  const firstSubcategory = firstCategory.subcategories[0]

  router.replace({ params: { subcategoryId: firstSubcategory.id.toString() } })
}

function toggleExpandAll(expandAll: boolean) {
  if (expandAll) {
    expandAllTopics(activeTopics.value)
  } else {
    data.expandedTopicIds = []
  }
}

function resetActiveSubcategory() {
  mainStore.updateActiveSubcategory(null)
  scrollToTop()
}

function scrollToTop() {
  setTimeout(() => {
    if (topicContainerRef.value && !route.hash) {
      topicContainerRef.value.scrollTo({
        top: 0,
        left: 0,
        behavior: 'auto'
      })
    }
  })
}

function onCategoryCollapseChange(category: Category) {
  data.activeCategory = category

  if (!category) {
    return
  }

  if (isMobile) {
    router.push({ query: { categoryId: category.id.toString() } })
  } else {
    const firstSubcategory = category.subcategories[0]

    router.push({ params: { subcategoryId: firstSubcategory.id.toString() } })
  }
}

function isTopicRegularLockcodeOrReservation(topic: Topic) {
  return [
    TopicContentTypeEnum.REGULAR,
    TopicContentTypeEnum.LOCK_CODE,
    TopicContentTypeEnum.RESERVATION
  ].includes(topic.dynamic_content_type)
}

function handleShareClick(payload: { type: ShareTypesEnum; topicId?: number }) {
  // get current domain url
  const basicShareLink = window.location.origin + '/guest/' + helperStore.guideToken

  switch (payload.type) {
    case ShareTypesEnum.GUIDE:
      data.shareLink = basicShareLink
      break
    case ShareTypesEnum.CATEGORY:
      data.shareLink = basicShareLink + '/info?categoryId=' + data.activeCategory.id
      break
    case ShareTypesEnum.SUBCATEGORY:
      data.shareLink = basicShareLink + '/info/' + mainStore.activeSubcategory.id
      break
    case ShareTypesEnum.TOPIC:
      data.shareLink =
        basicShareLink + '/info/' + mainStore.activeSubcategory.id + '#' + payload.topicId
      break
  }
}

function handleTryNowClick() {
  window.dataLayer.push({ event: 'guide_banner_click' })
  window.open('https://hub.touchstay.com/sign-up?utm_source=gv_banner', '_blank')
}
</script>

<style lang="scss">
.category-card {
  width: 50%;
  height: 33%;
  max-height: 140px;
  min-height: 140px;
  padding: 20px;
  z-index: 0;
  position: relative;
  cursor: pointer;
  transition: all 0.1s ease-in-out;
  -webkit-tap-highlight-color: transparent;

  &:first-child {
    margin-top: 0;
  }

  &.active {
    background-color: white;
    box-shadow: $info-block-shadow;
    border-radius: $border-radius;
    transform: scale(1.02);
    padding-bottom: 0;

    &:before {
      display: none;
    }

    &:after {
      display: none;
    }
  }

  &__thumbnail {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    text-align: center;
    min-height: 100%;

    &--full {
      display: none;
    }
  }

  &__name {
    user-select: none;
    max-width: 100%;
    line-height: 1.3;
    overflow: hidden;
  }

  &__subcategory {
    font-size: rem(15);
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    padding: 20px 0;

    i {
      font-size: rem(24);
      font-weight: bold;
    }

    &.full {
      &:first-child {
        padding-top: 20px;
      }
    }

    &:first-child {
      padding-top: 0;
    }

    &:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }
  }

  &__subcategories {
    position: absolute;
    top: 100%;
    z-index: -1;
    width: 200%;
    transform: translate(-25px, 0);
    background-color: white;
    padding: 25px;
    border-radius: $border-radius;
    overflow: hidden;

    &:before {
      content: '';
      width: 45%;
      position: absolute;
      height: 1px;
      right: 3%;
      top: 0;
      box-shadow: $info-block-shadow;
    }

    &:after {
      content: '';
      width: 100%;
      position: absolute;
      height: 1px;
      right: 3%;
      bottom: 0;
      box-shadow: $info-block-shadow;
    }
  }

  &__ic {
    width: 100%;
    font-size: rem(50);
    margin-bottom: 25px;
  }

  &__scroll {
    height: 100%;
    background-color: #f9f9f9;
    overflow: auto;
    border-radius: $border-radius;
  }

  &__wrapper {
    display: flex;
    flex-wrap: wrap;
    border-radius: $border-radius;
  }
}

.accessibility-link {
  color: inherit;
  position: absolute;
}

.el-dropdown {
  color: white;

  &-menu {
    &__item {
      &:hover {
        color: #606266 !important;
        background-color: white !important;
      }
    }
  }
}

.info-view {
  height: 100%;
  padding-right: 15px;
  padding-left: 15px;
  margin-right: auto;
  margin-left: auto;
  position: relative;

  i {
    cursor: pointer;
  }

  .el-collapse {
    border: none;

    &-item {
      max-width: 100%;

      &__header {
        border: none;
        font-size: 1.125rem;
        line-height: 1.5;
        text-align: left;
        position: relative;
        padding: 10px 5px 10px 10px;
      }

      &__content {
        font-weight: normal;
        border: none;

        img {
          max-width: 100%;
        }
      }

      .el-collapse-item__wrap {
        border: none;
        border-radius: $border-radius;
      }
    }
  }

  .info-card {
    box-shadow: 0 0 5px 1px rgba(0, 0, 0, 0.1);
    background-color: white;
    padding: 0;
    border-radius: $border-radius;
    font-weight: bold;
    font-size: rem(16);
    margin-bottom: 14px;

    .title-container {
      max-width: 80%;
    }

    i {
      font-size: 1.5rem;
    }

    &--ic {
      font-size: 1.5rem;
    }

    .title-text {
      border-bottom: 1px solid v-bind('theme.accentColor');
      padding-bottom: 5px;
    }
  }

  &__category {
    height: 70px;
    color: white;
    padding: 0 5px 0 15px;
    border-radius: $border-radius $border-radius 0 0;
    box-shadow: $info-header-shadow;

    .expand-row {
      i {
        margin-right: 10px;
        font-size: 1.625rem;
        text-shadow: $text-shadow--icon;
      }
    }

    h2 {
      text-overflow: ellipsis;
      font-size: rem(20);
      font-weight: bold;
      overflow: hidden;
      color: inherit;
      max-width: 80%;
      margin: 0;
      white-space: nowrap;
      text-shadow: $text-shadow--black;
    }
  }

  &__row {
    display: flex;
    flex-wrap: wrap;
    position: relative;
    background-color: #f3f3f3;
  }

  &__column {
    flex-flow: column;
    position: relative;
    padding: 15px 15px 0;
    height: 100%;

    &.categories {
      flex: 0 0 41.66667%;
      max-width: 41.66667%;
      overflow: auto;
    }

    &.category-topic {
      flex: 0 0 58.33333%;
      max-width: 58.33333%;
    }
  }

  &__categories {
    position: relative;
    height: 100%;
    border-radius: $border-radius;
    box-shadow: $info-block-shadow;
  }

  &__content {
    &--wrapper {
      border-radius: $border-radius;
    }
  }
}

.try-now-banner {
  height: $banner-height;
  background-color: #f9d154;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: v-bind('helperStore.headerFont');
  font-size: 0.875rem;
  position: relative;
  color: $primary;
  font-weight: bold;

  a {
    padding: 4px 8px;
    margin: 0 10px;
    color: $primary-accent;
    background-color: $primary;
    border-radius: 8px;
    cursor: pointer;

    &:hover {
      opacity: 0.7;
    }
  }

  i {
    position: absolute;
    font-size: 1.25rem;
    right: 5px;
  }
}

@include mediaMobileTablet {
  .hidden {
    display: none;
  }

  .info-view {
    &__row {
      display: block;
      height: calc(100% - #{$header-height});
      padding: $padding $padding 0 $padding;

      &--with-banner {
        height: calc(100% - #{$header-height} - #{$banner-height});
      }
    }

    &__column {
      &.categories,
      &.category-topic {
        width: 100%;
        height: 100%;
        max-width: 100%;
        padding: 0;
      }

      &.categories {
        box-shadow: 0 0 10px rgb(0 0 0 / 10%);
      }
    }

    &__category {
      display: none;
    }

    &__content {
      height: 100%;
      overflow: auto;
      scroll-behavior: smooth;
    }
  }

  .category-card {
    &__wrapper {
      -webkit-overflow-scrolling: touch;
    }

    &__ic {
      margin-bottom: 15px !important;
      font-size: 35px !important;
    }

    &:after {
      content: '';
      background-color: rgba(0, 0, 0, 0.1);
      width: calc(100% - 20px);
      height: 1px;
      position: absolute;
      bottom: 0;
      right: -1px;
      transform: translateX(-10px);
    }
  }

  .el-collapse {
    margin: 3px;
  }
}

@include mediaDesktop {
  .category-card {
    &__wrapper {
      display: block;
      padding: 14px;
    }
  }

  .info-view {
    &__categories {
      box-shadow: 0 0 10px rgb(0 0 0 / 10%);
      min-height: unset;
      height: 100%;
      border-radius: 0;
    }

    &__content {
      padding: 14px 14px 0 14px;
      position: relative;
      background-color: #f9f9f9;
      height: calc(100% - #{$header-height});
      overflow: auto;
      box-shadow: 0 0 10px rgb(0 0 0 / 10%);
    }

    &__row {
      height: 100%;
    }
  }
}

@include mediaTablet {
  .info-view {
    &__categories {
      width: 100%;
      height: 100%;
    }
  }

  .category-card:last-child:after {
    display: block;
  }
}

@include mediaTablet {
  .category-card {
    width: calc(100% / 3);
    max-width: calc(100% / 3);
    flex: 1 0 auto;
    min-height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;

    &:before {
      width: 1px;
      height: calc(100% - 30px);
      right: 0;
      top: 15px;
      z-index: 0;
      content: '';
      background-color: rgba(0, 0, 0, 0.1);
      position: absolute;
      left: 0;
    }

    &.active {
      border-radius: 0;
    }

    &__subcategories {
      position: absolute;
      top: 100%;
      width: 300%;
      transform: none !important;
      border-radius: 0;
    }

    &:nth-child(1n) {
      .category-card__subcategories {
        left: 0;
      }
    }

    &:nth-child(3n + 2) {
      .category-card__subcategories {
        left: -100%;
      }
    }

    &:nth-child(3n) {
      .category-card__subcategories {
        left: -200%;
      }
    }
  }
}

@include mediaDesktop {
  .category-card {
    width: 100%;
    background-color: white;
    max-width: 100%;
    height: unset;
    max-height: unset;
    min-height: unset;
    border-radius: $border-radius;
    margin-top: 15px;
    padding: 14px;
    box-shadow: 0 0 5px 1px rgba(0, 0, 0, 0.1);

    &__scroll {
      height: calc(100% - #{$header-height});
    }

    .el-collapse-item {
      width: 100%;

      &__header {
        line-height: 28px;
      }
    }

    &__subcategories {
      display: none;

      &--full {
        padding-left: 50px;
      }
    }

    &__subcategory {
      &.full {
        padding-left: 1rem;
        font-size: rem(16);
        padding-right: 0.5rem;

        i {
          font-weight: normal;
          font-size: rem(18);
        }
      }
    }

    &__thumbnail {
      display: none;
      align-items: center;
      flex-direction: unset;
      justify-content: flex-start;

      &--full {
        display: flex;
        align-items: center;
        width: 100%;
        font-weight: 500;
      }
    }

    &__ic {
      width: auto;
      margin-bottom: 0;
      font-size: 2rem;
      padding-right: 1rem;
      margin-right: 1rem;
      border-right: 0.5px solid #d1d1d1;
    }

    &.active {
      transform: scale(1);
      z-index: 100;
      border-radius: $border-radius;
      box-shadow: 0 0 5px 1px rgba(0, 0, 0, 0.1);
    }
  }
}

@include mediaMobile {
  .category-card {
    &:nth-child(odd):before {
      content: '';
      background-color: rgba(0, 0, 0, 0.1);
      width: 1px;
      transform: translateY(-15px);
      height: calc(100% - 15px);
      position: absolute;
      right: 0;
      z-index: 0;
    }

    &:nth-child(even) {
      .category-card__subcategories {
        transform: translateX(calc(-50% - 15px));
      }
    }
  }
}

@include mediaTablet {
  .info-view {
    width: 750px;
  }
}

@include mediaDesktop {
  .info-view {
    width: 1170px;
  }
}

@include mediaMobileTablet {
  .info-view {
    padding: 0;
  }
}
</style>
